<template>
  <div class="results-dashboard">
    <router-link :to="{ name: 'AdminDashboard' }" class="back-link">&larr; Kembali ke Dashboard Utama</router-link>
    <h1>Hasil Tes Peserta</h1>

    <!-- Filter Kontrol -->
    <!-- Di dalam ResultsDashboard.vue -> <template> -->

      <!-- Filter Kontrol -->
      <div class="card filter-card">
        <!-- Filter Gerai -->
        <div class="filter-group">
          <label for="gerai-filter">Pilih Gerai</label>
          <select 
            id="gerai-filter" 
            v-model="selectedGeraiId"
            :disabled="authStore.user && authStore.user.gerai_id"
          >
            <!-- Tampilkan opsi ini hanya jika bukan admin gerai -->
            <option v-if="authStore.user && !authStore.user.gerai_id" :value="null">-- Semua Gerai --</option>
            <option v-for="gerai in allGerai" :key="gerai.id" :value="gerai.id">
              {{ gerai.name }}
            </option>
          </select>
        </div>

        <!-- Filter Paket Tes -->
        <div class="filter-group">
          <label for="package-filter">Pilih Paket Tes</label>
          <select id="package-filter" v-model="selectedPackageId">
            <option :value="null">-- Semua Paket --</option>
            <option v-for="pkg in filteredPackages" :key="pkg.id" :value="pkg.id">
              {{ pkg.name }}
            </option>
          </select>
        </div>

        <!-- Filter Status Pengerjaan -->
        <div class="filter-group">
          <label for="status-filter">Status Pengerjaan</label>
          <select id="status-filter" v-model="statusFilter">
            <option :value="null">Semua Status</option>
            <option value="Selesai">Selesai</option>
            <option value="Sedang Mengerjakan">Sedang Mengerjakan</option>
            <option value="Baru">Belum Dikerjakan</option>
          </select>
        </div>
        
        <!-- Filter Status SETU -->
        <div class="filter-group">
          <label for="setu-filter">Status SETU</label>
          <select id="setu-filter" v-model="setuStatusFilter">
            <option :value="null">Semua</option>
            <option value="manual">VIP / Manual</option>
            <option value="Terkirim">Terkirim</option>
            <option value="Belum Dikirim">Belum Dikirim</option>
            <option value="Gagal">Gagal</option>
          </select>
        </div>

        <div class="filter-group">
          <label for="date-filter">Rentang Tanggal Tes</label>
          <VueDatePicker 
            v-model="dateRange" 
            range 
            :enable-time-picker="false"
            placeholder="Pilih rentang tanggal"
            auto-apply
            format="dd-MM-yyyy"
          />
        </div>
      </div>
  

    <!-- Daftar Peserta -->
    <div class="card">
      <header class="card-header">
        <h2>Daftar Peserta</h2>
        <div class="header-actions"> <!-- Buat container baru untuk search dan export -->
          <input v-model.lazy="searchTerm" type="text" placeholder="Cari Nama atau NIK..." class="search-input"/>
          <button @click="exportPackageExcel" class="export-btn" style="background-color: #27ae60; margin-right: 10px;" :disabled="!selectedPackageId">
            <i class="fas fa-file-excel"></i> Laporan Psikogram
          </button>
          <button @click="exportData" class="export-btn">
            <i class="fas fa-file-excel"></i> Ekspor Semua (CSV)
          </button>
        </div>
      </header>

      <div v-if="isLoadingParticipants" class="loading-placeholder">Memuat data peserta...</div>
      <div v-else>
        <div v-if="participants.length > 0" class="table-container">
          <table>
            <thead>
              <tr>
                <th>Nama Peserta</th>
                <th>Nomor Tes (NIK)</th>
                <th>Tanggal Tes</th>
                <th>Status Pengerjaan</th>
                <th>Kesimpulan</th>
                <th>Status SETU</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in participants" :key="p.id">
                <td>{{ p.name }}</td>
                <td><code>{{ p.test_number }}</code></td>
                <td>{{ formatDate(p.created_at) }}</td>
                <td><span class="status-badge" :class="p.overall_status.replace(' ', '-').toLowerCase()">{{ p.overall_status }}</span></td>
                <td><span class="status-badge" :class="p.conclusion.toLowerCase()">{{ p.conclusion }}</span></td>
                <td>{{ p.setu_status }}</td>
                <td class="actions">
                  <router-link :to="{ name: 'ParticipantDetail', params: { id: p.id } }">
                    <button class="action-btn view-btn">Detail</button>
                  </router-link>
                  <button @click="deactivateParticipant(p.id)" class="action-btn delete-btn">Nonaktifkan</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="empty-state">Belum ada peserta yang cocok dengan filter ini.</p>
        
        <div v-if="totalParticipants > 0" class="pagination-controls">
          <span>{{ pageInfo }}</span>
          <div class="buttons">
            <button @click="prevPage" :disabled="currentPage === 1">&larr; Sebelumnya</button>
            <button @click="nextPage" :disabled="currentPage >= totalPages">Berikutnya &rarr;</button>
          </div>
        </div>
      </div>
      <p v-if="error" class="error">{{ error }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import apiClient from '@/services/api.js';
import { useAuthStore } from '@/stores/auth';

import { VueDatePicker } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';


// ========== STATE UTAMA ==========
const authStore = useAuthStore();

const allPackages = ref([]);
const allGerai = ref([]);
const participants = ref([]);

const selectedGeraiId = ref(null);
const selectedPackageId = ref(null);
const statusFilter = ref(null);
const setuStatusFilter = ref(null);
const searchTerm = ref('');
const dateRange = ref();

const totalParticipants = ref(0);
const currentPage = ref(1);
const itemsPerPage = ref(10);

const isLoadingPackages = ref(true);
const isLoadingGerai = ref(true);
const isLoadingParticipants = ref(false);
const error = ref('');

// ========== COMPUTED PROPERTIES ==========

const filteredPackages = computed(() => {
  if (!selectedGeraiId.value) {
    return allPackages.value;
  }
  return allPackages.value.filter(pkg => pkg.gerai_id === selectedGeraiId.value);
});

const totalPages = computed(() => Math.ceil(totalParticipants.value / itemsPerPage.value));

const pageInfo = computed(() => {
  if (isLoadingParticipants.value) return 'Memuat...';
  if (totalParticipants.value === 0) return 'Tidak ada peserta.';
  const start = (currentPage.value - 1) * itemsPerPage.value + 1;
  const end = Math.min(start + itemsPerPage.value - 1, totalParticipants.value);
  return `Menampilkan ${start}-${end} dari ${totalParticipants.value} peserta`;
});

// ========== API FUNCTIONS ==========

const fetchAllGerai = async () => {
  isLoadingGerai.value = true;
  try {
    const response = await apiClient.get('/admin/gerai/');
    allGerai.value = response.data;
  } catch (err) {
    error.value = "Gagal memuat daftar gerai.";
  } finally {
    isLoadingGerai.value = false;
  }
};

const fetchAllPackages = async () => {
  isLoadingPackages.value = true;
  try {
    const response = await apiClient.get('/admin/packages/?limit=1000');
    allPackages.value = response.data.packages;
  } catch (err) {
    error.value = "Gagal memuat daftar paket tes.";
  } finally {
    isLoadingPackages.value = false;
  }
};

const fetchParticipants = async () => {
  isLoadingParticipants.value = true;
  error.value = '';
  try {
    const params = new URLSearchParams({
      skip: (currentPage.value - 1) * itemsPerPage.value,
      limit: itemsPerPage.value,
    });
    
    if (searchTerm.value) params.append('search', searchTerm.value);
    if (selectedPackageId.value) params.append('package_id', selectedPackageId.value);
    if (statusFilter.value) params.append('overall_status', statusFilter.value);
    if (setuStatusFilter.value) params.append('result_type', setuStatusFilter.value);
    if (selectedGeraiId.value) params.append('gerai_id', selectedGeraiId.value);
    if (dateRange.value && dateRange.value[0] && dateRange.value[1]) {
      params.append('start_date', dateRange.value[0].toISOString().slice(0, 10));
      params.append('end_date', dateRange.value[1].toISOString().slice(0, 10));
    }

    const apiUrl = `/admin/participants/?${params.toString()}`;
    console.log("MEMANGGIL API PESERTA:", apiUrl);

    const response = await apiClient.get(apiUrl);
    participants.value = response.data.participants;
    totalParticipants.value = response.data.total_count;
  } catch (err) {
    error.value = "Gagal memuat data peserta.";
    participants.value = [];
    totalParticipants.value = 0;
  } finally {
    isLoadingParticipants.value = false;
  }
};


const exportPackageExcel = async () => {
  if (!selectedPackageId.value) {
    alert('Silakan Pilih Paket Tes terlebih dahulu untuk mengunduh laporan psikogram!');
    return;
  }
  try {
    const apiUrl = `/admin/reports/packages/${selectedPackageId.value}/excel`;
    const response = await apiClient.get(apiUrl, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const filename = `Laporan_Psikogram_${selectedPackageId.value}_${new Date().toISOString().slice(0,10)}.xlsx`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (err) {
    console.error('Gagal mendownload Excel', err);
    alert('Gagal mendownload laporan psikogram. Pastikan Anda memiliki akses yang benar.');
  }
};

const exportData = async () => {
  const params = new URLSearchParams();
  
  if (selectedGeraiId.value) params.append('gerai_id', selectedGeraiId.value);
  if (selectedPackageId.value) params.append('package_id', selectedPackageId.value);
  if (statusFilter.value) params.append('overall_status', statusFilter.value);
  if (setuStatusFilter.value) params.append('result_type', setuStatusFilter.value);
  if (searchTerm.value) params.append('search', searchTerm.value);
  if (dateRange.value && dateRange.value[0] && dateRange.value[1]) {
    params.append('start_date', dateRange.value[0].toISOString().slice(0, 10));
    params.append('end_date', dateRange.value[1].toISOString().slice(0, 10));
  }

  try {
    const apiUrl = `/admin/participants/download/csv?${params.toString()}`;
    const response = await apiClient.get(apiUrl, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const filename = `export_peserta_${new Date().toISOString().slice(0,10)}.csv`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    alert("Gagal mengunduh data. Coba lagi.");
    console.error("Export error:", err);
  }
};

// ========== ACTION HANDLERS ==========

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const deactivateParticipant = async (participantId) => {
  if (window.confirm("Yakin ingin menonaktifkan peserta ini? Mereka akan disembunyikan dari daftar utama.")) {
    try {
      // Panggil endpoint PUT yang baru
      await apiClient.put(`/admin/participants/${participantId}/deactivate`);
      await fetchParticipants(); // Muat ulang daftar
    } catch (err) {
      alert("Gagal menonaktifkan peserta.");
    }
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const options = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: '2-digit', 
    minute: '2-digit', 
    hour12: false,
    // Secara eksplisit minta zona waktu Asia/Jakarta
    timeZone: 'Asia/Jakarta' 
  };
  return new Intl.DateTimeFormat('id-ID', options).format(date);
};

// ========== WATCHERS & LIFECYCLE ==========

// SATU WATCH UNTUK SEMUA FILTER
watch([selectedGeraiId, selectedPackageId, searchTerm, statusFilter, setuStatusFilter, dateRange], (newValues, oldValues) => {
  // Reset halaman ke 1 jika ada filter yang berubah (kecuali pagination)
  // Cek apakah 'selectedGeraiId' berubah, jika ya, reset 'selectedPackageId'
  if (newValues[0] !== oldValues[0]) {
    selectedPackageId.value = null;
  }
  currentPage.value = 1;
  fetchParticipants();
}, { deep: true });

// WATCH TERPISAH HANYA UNTUK PAGINATION
watch(currentPage, () => {
  fetchParticipants();
});

onMounted(async () => {
  await authStore.fetchUser();
  
  await Promise.all([
    fetchAllPackages(),
    fetchAllGerai()
  ]);
  
  if (authStore.user && authStore.user.gerai_id) {
    selectedGeraiId.value = authStore.user.gerai_id;
  } else {
    // Untuk Psikolog/Superadmin, langsung picu pengambilan data awal
    fetchParticipants();
  }
});
</script>

<style scoped>
.results-dashboard {
  max-width: 1200px;
  margin: auto;
  padding: 30px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

h1 { margin: 0 0 20px 0; font-size: 1.8em; }
h2 { margin: 0 0 10px 0; font-size: 1.4em; }
.subtitle { margin: 0 0 20px 0; color: #555; }
.back-link { display: inline-block; margin-bottom: 20px; color: #3498db; }

.card {
  margin-top: 25px;
  padding: 25px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(0,0,0,0.04);
  border: 1px solid #eee;
}

.package-select {
  width: 100%;
  padding: 12px;
  font-size: 1em;
  border-radius: 8px;
  border: 1px solid #ccc;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
  padding-bottom: 15px;
  margin-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.filter-controls input {
  padding: 10px 14px;
  border-radius: 6px;
  border: 1px solid #ccc;
  min-width: 250px;
}

.loading-placeholder, .empty-state {
  text-align: center;
  padding: 40px;
  color: #777;
  font-style: italic;
}

.table-container { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; }
th, td { text-align: left; padding: 14px 16px; border-bottom: 1px solid #f0f0f0; vertical-align: middle; }
th { background-color: #f8f9fa; font-weight: 600; color: #555; font-size: 0.9em; }
tr:last-child td { border-bottom: none; }
tr:hover { background-color: #f9fafb; }
code { background-color: #eef1f2; padding: 2px 6px; border-radius: 4px; }

.actions { display: flex; gap: 8px; justify-content: flex-end; }
.action-btn {
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  font-size: 0.9em;
  cursor: pointer;
  transition: opacity 0.2s;
}
.action-btn:hover { opacity: 0.8; }
.view-btn { background-color: #17a2b8; color: white; }
.delete-btn { background-color: #e74c3c; color: white; }

.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}
.pagination-controls .buttons button {
  padding: 8px 14px;
  border: 1px solid #ddd;
  background-color: #fff;
  border-radius: 6px;
  cursor: pointer;
}
.pagination-controls button:disabled { cursor: not-allowed; opacity: 0.5; }
.error { color: red; }

.filter-card {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: flex-end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-grow: 1; /* Agar setiap filter mencoba mengambil ruang yang sama */
}

.filter-group label {
  font-weight: 500;
  font-size: 0.9em;
  color: #555;
}

.filter-group select {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1em;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
}

.header-actions {
  display: flex;
  gap: 15px;
  align-items: center;
}

.search-input {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
}

.export-btn {
  padding: 10px 15px;
  background-color: #1D6F42; /* Warna hijau Excel */
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}
.export-btn:hover {
  background-color: #165934;
}

.status-badge {
  padding: 5px 10px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: bold;
  color: #333;
  background-color: #e9ecef;
}
.status-badge.selesai {
  background-color: #d4edda;
  color: #155724;
}
.status-badge.sedang-mengerjakan {
  background-color: #fff3cd;
  color: #856404;
}
.status-badge.lulus {
  background-color: #28a745;
  color: white;
}
.status-badge.tidak-lulus {
  background-color: #dc3545;
  color: white;
}

</style>